import {
  FluidAlignment,
  FluidCurrencyFieldConfig,
  FluidFieldValidation,
  FluidFormElementOutputType,
  FluidFormElementState,
  FluidFormElementTranslationOptions,
  FluidFormState,
  FluidInputType,
  FluidLanguage,
  FluidPopoverConfig,
  FluidRoundingMethod,
  FluidTextOverflow,
  FluidTheme,
  FluidThemeInterface,
  FluidUpdateStrategy,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { FluidFormatters } from '@lmig/fluid-core/lib/utils/formatting.utils';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import {
  CancelInput,
  FormLabel,
  Validation,
} from '../../common/fluid-common-form.components';
import { FluidCurrencyCode } from '../../model/forms/fluid-currency.locales';
import { FluidFormElementsInterface } from '../../model/forms/fluid-form-elements.interface';
import {
  setI18nState,
  translateProperty,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { EventUtils } from '../../utils/events/event-utils';
import {
  currencyOptions,
  formatAsCurrency,
  removeCurrencySymbol,
} from '../../utils/formatting/ii8n.utils';
import {
  applyElementOutputFormatting,
  getFormInputId,
  getValue,
  handleAutoFocus,
  markTouchedAndValidate,
  preventInvalidShortHandInput,
  reset,
  setValue,
  update,
  validateElement,
} from '../../utils/forms/form-method.utils';
import {
  canBeCoercedToNumber,
  elementClass,
  setInitialElementState,
} from '../../utils/forms/form-utils';
import { Utils } from '../../utils/utils';
import { handleNaN } from '../fluid-form/util/fluid-form.utils';

/**
 * @displayName Currency Field
 *
 * @advancedUsage /forms
 *
 * @builder true
 */
@Component({
  tag: 'fluid-currency-field',
  styleUrl: 'fluid-currency-field.less',
  shadow: true,
})
export class FluidCurrencyField
  implements
    FluidThemeInterface,
    ComponentInterface,
    FluidFormElementsInterface
{
  // ================================================================ //
  // -- Validation & Local Variables

  /**
   * Boolean that tracks whether the form field has received :focus and/or
   * been manipulated since initial page load.
   */
  touched = false;

  /**
   * Reference to the parent FLUID Form state.
   */
  _parentState: FluidFormState;

  /**
   * Reference to the core HTML <input> element.
   */
  _elementRef: HTMLInputElement;

  /**
   * Boolean that tracks whether the form field currently has :focus.
   */
  _hasFocus = false;

  /**
   * Boolean that tracks whether the form field has received autofocus
   */
  _autoFocus = false;

  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidFormElementTranslationOptions;
  _translateId: string;
  _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound: boolean;
  @State() _lang: FluidLanguage;

  // ================================================================ //
  // -- States

  /**
   * Derived form field state that tracks value, errors, validity, etc.
   * This common interface is shared among all form fields.
   */
  @State() _elementState: FluidFormElementState;

  @State() _valueString = '';
  @State() _type: FluidInputType = FluidInputType.TEXT;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * A unique name for the field (usually the dataKey for any form using
   * the input element
   */
  @Prop({ mutable: true }) controlName: string;

  /**
   * A label to show with the input field (where applicable)
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Sets the Form Label's text overflow behavior. By default, overflowing
   * label text will be truncated with ellipsis.
   */
  @Prop({ mutable: true }) labelTextOverflow: FluidTextOverflow;

  /**
   * Optional placeholder text to display within Currency Field's <input>.
   */
  @Prop({ mutable: true }) placeholder: string;

  /**
   * If true, Currency Field's <input> will be disabled.
   */
  @Prop({ mutable: true }) disabled = false;

  /**
   * If true, Currency Field's <input> will be in a "readonly" state.
   */
  @Prop({ mutable: true }) readonly = false;

  /**
   * Sets the initial value of the currency field (prior to formatting
   * to desired currency). NOTE: you do NOT need to add the currency
   * symbol yourself - you should only set the initial value as a number
   * (e.g. 0.00 or 0). If you set the initial value to anything other
   * than a number, it will return NaN - this is expected behaviour where
   * you have configured Not a Number (NaN) as your initial value. However,
   * to avoid displaying NaN to your users, the field will automatically
   * reset the "initalValue" to an empty string & display a console warning
   * in this scenario.
   */
  @Prop({ mutable: true }) initialValue: number | string;

  /**
   * What is the value of the field (where applicable)
   */
  @Prop({ mutable: true }) value: number | string;

  @Watch('value')
  onValueChanged(newValue: string | number, oldValue: string | number) {
    if (newValue === oldValue) return;

    if (this._elementState) {
      this.touched = false;
      this._elementState = {
        ...this._elementState,
        value: newValue?.toString(),
        touched: this.touched,
      };
      this.convertToCurrencyString();
    }
  }

  /**
   * An array of field validation options.
   */
  @Prop({ mutable: true }) validation: FluidFieldValidation[];

  /**
   * The type of the input field
   */
  @Prop({ mutable: true }) type: FluidInputType;

  /**
   * A muted label to show after the main form label
   */
  @Prop({ mutable: true }) supplementaryLabel: string;

  /**
   * If provided, Currency Field's Form Label will render a "helptext"
   * icon with attached Popover. To render a basic Popover with default
   * Prop values, simply provide the Popover's message as a string
   * value to this Prop. To render a Popover with customized Props,
   * provide a FluidPopoverConfig object to this Prop with the "message"
   * property set. For more information, please see Popover's API
   * documentation.
   */
  @Prop({ mutable: true }) helpText: string | FluidPopoverConfig;

  /**
   * Provide the currency code for the chosen currency
   */
  @Prop({ mutable: true }) currencySymbol: FluidCurrencyCode | string =
    FluidCurrencyCode.USD;

  @Watch('currencySymbol')
  onCurrencySymbolChange() {
    this._currencyOptions = currencyOptions(this.currencySymbol);
    this.convertToCurrencyString();
  }

  /**
   * If the chosen currency isn't natively supported by FLUID, provide the
   * locale to use in the formatting.
   */
  @Prop({ mutable: true }) currencyLocale: string;

  /**
   * Set to false, if we want a currency field, but without a currency
   * symbol shown.
   */
  @Prop({ mutable: true }) showCurrency = true;

  /**
   * If the field is an inline one, remove all margins so it sits
   * nicely in its container.
   */
  @Prop({ mutable: true }) inlineField: boolean;

  /**
   * If true, allows the clear icon to be shown for non-required fields
   */
  @Prop({ mutable: true }) allowCancel?: boolean;

  /**
   * Allows the step increment/decrement to be configured.
   */
  @Prop({ mutable: true }) step = '.01';

  /**
   * When true, Currency Field's <input> will only show whole numbers
   * with zero decimal places. NOTE: the "roundingMethod" Prop determines
   * how the decimal values are rounded.
   */
  @Prop({ mutable: true }) wholeOnly: boolean;

  /**
   * If using the "wholeOnly" Prop, this Prop dictates how decimal values
   * should be rounded to the nearest whole number (e.g. round up or round
   * down). Defaults to always rounding down.
   */
  @Prop({ mutable: true }) roundingMethod: FluidRoundingMethod =
    FluidRoundingMethod.DEFAULT;

  /**
   * When the input has the numeric type: `type=number`, the stepper buttons are displayed.
   * By default, the value of this property is `true`.
   *
   * This rule only will be applied on desktop devices, this rule will be ignored on mobile devices (Phones, Tablets).
   * Stepper buttons will not be displayed on mobile devices, this is the standard behavior.
   */
  @Prop({ mutable: true }) displayStepperButtons = true;

  /**
   * If true, this field will automatically have focus on page load. Note: only one form field should have this
   * autoFocus set to true on a page. If multiple fields have autoFocus = true, then there could be unexpected behaviour.
   */
  @Prop({ mutable: true }) autoFocus = false;

  /**
   * Determines the updating strategy for the field;
   * 'input' - the field will update the element state on each change to its value.
   * 'blur' - the field will update the element state when the user leaves the field.
   */
  @Prop({ mutable: true }) updateStrategy: FluidUpdateStrategy =
    FluidUpdateStrategy.INPUT;

  /**
   * Determines whether the output value should remain as a string or be
   * coerced to a number when emitted.
   */
  @Prop({ mutable: true }) outputType: FluidFormElementOutputType =
    FluidFormElementOutputType.STRING;

  /**
   * If true, this field will accept shorthand numbers as input
   */
  @Prop({ mutable: true }) shortHandNumbers?: boolean;

  /**
   * Allows form element configuration to be passed as a single property.
   */
  @Prop({ mutable: true }) config: FluidCurrencyFieldConfig;

  @Watch('config')
  onConfigChanged() {
    Utils.selectiveMapConfigToLocal(this, this.config, ['value']);
  }

  // TODO
  // Stepper buttons UI for Mobile devices
  // https://developer.apple.com/design/human-interface-guidelines/components/selection-and-input/steppers/

  _currencyOptions = currencyOptions(this.currencySymbol);

  // ================================================================ //
  // -- Events

  /**
   * Emits the element state when its value changes
   */
  @Event() valueChange: EventEmitter<FluidFormElementState>;
  _lastEmittedState: FluidFormElementState;

  /**
   * Value change handler - explicitly checks if the element state has actually
   * changed before emitting to ensure data flow only occurs when required.
   * @param elementState
   */
  emitValueChange(elementState: FluidFormElementState = this._elementState) {
    if (Utils.isEqual(this._lastEmittedState?.value, elementState?.value)) {
      Utils.debug(
        '[CURRENCY] Not emitting value change - state has not changed',
        elementState
      );
    } else {
      if (
        this.outputType === FluidFormElementOutputType.NUMBER &&
        canBeCoercedToNumber(elementState.value)
      ) {
        elementState.value = Number(elementState.value);
      }
      this.valueChange.emit(elementState);
      this._lastEmittedState = elementState;
    }
  }

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config, ['value']);
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      init: () => setI18nState.bind(this)(),
      language: () => setI18nState.bind(this)(),
    });
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Set the initial element state on first load
   */
  componentWillLoad() {
    this.validateInitialValue();
    this._elementState = setInitialElementState(
      isNullOrUndefined(this.initialValue, true) ? null : this.initialValue,
      this.controlName
    );
    this._lastEmittedState = this._elementState;
    this._autoFocus = this.autoFocus;

    if (!isNullOrUndefined(this.initialValue)) {
      this.convertToCurrencyString();
    }
  }

  /**
   * When the component re-renders, ensure the formatting is applied.
   */
  componentDidRender() {
    if (isNullOrUndefined(this._elementState.value)) {
      this._elementRef.value = '';
    }
    handleAutoFocus(this);
  }

  // ================================================================ //
  // -- Public Methods

  @Method() async setValue(value: any, opts?: Record<string, any>) {
    return setValue(
      this,
      value,
      () => {
        if (!this._hasFocus) {
          this.convertToCurrencyString();
        }
      },
      opts
    );
  }

  @Method() async getValue() {
    return getValue(this);
  }

  @Method() async validate() {
    return validateElement(this);
  }

  @Method() async reset(clear = false) {
    this._valueString = '';
    return reset(this, clear, () => {
      if (!this._hasFocus) {
        this.convertToCurrencyString();
      }
    });
  }

  @Method() async markTouchedAndValidate() {
    return markTouchedAndValidate(this);
  }

  @Method() async setParentValue(formState: FluidFormState) {
    this._parentState = formState;
  }

  @Method() async getNativeInput() {
    return this._elementRef;
  }

  // ================================================================ //
  // -- Local Methods

  validateInitialValue() {
    if (!this.shortHandNumbers && isNaN(Number(this.initialValue))) {
      Utils.consoleWarn(
        this.host,
        `The configured initialValue ('${this.initialValue}') is not a number so will be reset to empty to avoid $NaN`,
        true
      );
      this.initialValue = null;
    }

    if (!this.shortHandNumbers && isNaN(Number(this.value))) {
      Utils.consoleWarn(
        this.host,
        `The configured value ('${this.value}') is not a number so will be reset to empty to avoid $NaN`
      );
      this.value = null;
    }

    if (!isNullOrUndefined(this.value, true)) {
      this.initialValue = this.value;
    }
  }

  /**
   * @returns string or number
   */
  getFieldValue(): string | number {
    if (this._type === FluidInputType.NUMBER) {
      return this._elementState.value === 0
        ? '0' // to avoid empty string when is 0 === number type
        : handleNaN(parseFloat(this._elementState.value as string));
    } else {
      return this._valueString;
    }
  }

  convertToCurrencyNumber() {
    if (!this.readonly && !this.disabled) {
      if (this.shortHandNumbers) {
        this._type = FluidInputType.TEXT;
        this._elementRef.value =
          !!this._elementState.value || this._elementState.value === 0
            ? handleNaN<string>(Number(this._elementState.value).toString())
            : null;
      } else {
        this._type = FluidInputType.NUMBER;

        this._elementRef.value = this.getFieldValue()?.toString();
      }
      if (Utils.browserIsMsie()) {
        const value = isNullOrUndefined(this._elementState.value)
          ? ''
          : this._elementState.value;
        this.value = (this.host as HTMLInputElement).value;
        this._elementRef.value = value === 0 ? '' : value;
      }
    }
  }

  convertFromShorthand(value: any) {
    if (this.shortHandNumbers) {
      value = FluidFormatters.formatShorthandNumber(value);
      this._elementState.value = handleNaN(value.toFixed(2));
    } else {
      this._elementState.value = handleNaN(parseFloat(value).toFixed(2));
    }
    return value;
  }

  convertToRounded(value: any) {
    if (this.wholeOnly && !isNaN(value)) {
      value = FluidFormatters.applyRounding(
        handleNaN(this._elementState.value),
        this.roundingMethod
      );
      this._elementState.value = handleNaN(value);
    }
    return value;
  }

  convertToCurrencyString() {
    this._type = FluidInputType.TEXT;
    let value = this._elementState.value;
    value = this.convertFromShorthand(value);
    value = this.convertToRounded(value);

    this.validate().then(() => {
      this.emitValueChange(applyElementOutputFormatting(this));
    });

    const formattedValue = formatAsCurrency(
      handleNaN(value),
      this.currencyLocale,
      this.currencySymbol,
      this._currencyOptions,
      this.wholeOnly
    );

    this._valueString = this.showCurrency
      ? formattedValue
      : removeCurrencySymbol(formattedValue);
    if (this.shortHandNumbers) {
      try {
        this._elementRef.value = this._valueString;
      } catch (TypeError) {
        return;
      }
    }
  }

  handleFocus(): void {
    if (this.disabled || this.readonly) return;
    this._hasFocus = true;
    this.convertToCurrencyNumber();
  }

  handleBlur(event: FocusEvent): void {
    const value = (event.target as HTMLInputElement).value;

    if (!value) {
      this.markTouchedAndValidate().then();
    }

    // remove the comma if the user has copied and pasted the value
    // Note: on edit mode, the user is restricted to type numbers only, which means no commas are allowed.
    const regex = new RegExp('[,]', 'g');

    if (regex.test(value)) {
      const _value = value.replace(regex, '');
      (event.target as HTMLInputElement).value = _value;
      this._elementState.value = handleNaN(_value);
    }

    if (!this.readonly) {
      this._hasFocus = false;
      if (this.updateStrategy?.toUpperCase() === FluidUpdateStrategy.BLUR) {
        this._valueString = (event.target as HTMLInputElement).value; // remove cache for empty string;
        update(this, event, null, true, () => this.emitValueChange());
      }
      this.convertToCurrencyString();
    }
  }

  handleInput(event: InputEvent): void {
    if (!this.readonly) {
      if (this.updateStrategy?.toUpperCase() !== FluidUpdateStrategy.BLUR) {
        this._valueString = (event.target as HTMLInputElement).value; // remove cache for empty string;
        update(this, event, null, true, () => this.emitValueChange());
      }
    }
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (this.shortHandNumbers && !this.readonly) {
      preventInvalidShortHandInput(event);
    }
  }

  /**
   * Prevents "wheel" events (i.e. scrolling) from incrementing /
   * decrementing embedded <input type="number">. Also removes :focus
   * from the <input> to allow the page to continue scrolling.
   * @param event - "wheel" event that occurred on embedded <input>
   */
  preventNumberScroll(event: WheelEvent): void {
    if (this._type !== FluidInputType.NUMBER || !this._hasFocus) return;

    event.preventDefault();
    this._elementRef.blur();
  }

  /**
   * Sets the element refernce to the local variable and adds any passive listeners required.
   * @param ref
   */
  setElementRef(ref: HTMLInputElement) {
    this._elementRef = ref;
    EventUtils.addListener('wheel', this._elementRef, (event: any) =>
      this.preventNumberScroll(event)
    );
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const otherClasses = {
      disabled: this.disabled,
      readonly: this.readonly,
      'hide-stepper-buttons': !this.displayStepperButtons,
    };

    const styles = elementClass(
      'fluid-currency-field',
      this.theme,
      this._elementState,
      this.inlineField,
      this.touched,
      otherClasses
    );
    return (
      <div class={styles.componentWrapper}>
        <div class={styles.formGroupClass}>
          <FormLabel host={this} />
          <div class={`input-wrapper text-align-${this.textAlign}`}>
            <input
              id={getFormInputId(this.config?.id, this.controlName)}
              class={styles.inputClass}
              type={this._type}
              step={this.step}
              name={this._elementState.name}
              value={this.getFieldValue()}
              ref={(el) => this.setElementRef(el)}
              onFocus={() => this.handleFocus()}
              onBlur={(event: FocusEvent) => this.handleBlur(event)}
              onInput={(event: InputEvent) => this.handleInput(event)}
              onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}
              placeholder={translateProperty.bind(this)('placeholder')}
              disabled={this.disabled}
              readOnly={this.readonly}
              required
            />
            <CancelInput host={this} />
          </div>
          <div
            class={{
              'form-element-notifications': true,
              'inline-field': this.inlineField,
            }}>
            <Validation host={this} fluidTranslate={this.fluidTranslate} />
          </div>
        </div>
      </div>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * @internal
   */
  @Prop({ mutable: true }) _standalone = true;

  /**
   * Sets <input> element text alignment. Used for editable Table cells.
   * @internal
   */
  @Prop({ mutable: true }) textAlign: FluidAlignment = FluidAlignment.LEFT;

  /**
   * @internal
   */
  @Prop({ mutable: true }) parentState: FluidFormState;

  /**
   * @internal
   */
  @Prop({ mutable: true }) autoValidateChildren: boolean;

  @Watch('parentState') async onParentStateChange(newState: FluidFormState) {
    this._parentState = newState;
    if (this.autoValidateChildren) {
      await this.validate();
    }
  }

  /**
   * @internal
   */
  @Prop({ mutable: true }) externalState: Record<string, any>;
}
